import { useEffect, useState } from 'react'
import { type MetaFunction, useSearchParams } from 'react-router'
import { ClientOnly } from 'remix-utils/client-only'
import { LoadingIndicator } from '#app/components/application/loading-indicator/loading-indicator.tsx'
import { Button } from '#app/components/base/buttons/button.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'

interface ConsentInfo {
	client: { name: string; icon?: string | null }
	scopes: string[]
}

function ConsentForm() {
	const [searchParams] = useSearchParams()
	const [consentInfo, setConsentInfo] = useState<ConsentInfo | null>(null)
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		async function fetchConsentInfo() {
			try {
				const clientId = searchParams.get('client_id')
				if (!clientId) {
					setError('Missing client information.')
					return
				}
				const url = `/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`
				const res = await fetch(url, { credentials: 'include' })
				if (!res.ok) {
					setError('Failed to load consent information.')
					return
				}
				const contentType = res.headers.get('content-type') || ''
				if (!contentType.includes('application/json')) {
					setError('Failed to load consent information.')
					return
				}
				const data = (await res.json()) as {
					client_id: string
					client_name: string
				}
				const scopeParam = searchParams.get('scope')
				const scopes = scopeParam ? scopeParam.split(' ') : []
				setConsentInfo({ client: { name: data.client_name }, scopes })
			} catch {
				setError('Failed to load consent information.')
			} finally {
				setLoading(false)
			}
		}
		void fetchConsentInfo()
	}, [searchParams])

	async function handleConsent(accept: boolean) {
		setSubmitting(true)
		setError(null)
		try {
			const res = await fetch('/api/auth/oauth2/consent', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					accept,
					oauth_query: searchParams.toString(),
				}),
				redirect: 'follow',
			})

			if (res.redirected) {
				window.location.href = res.url
				return
			}

			// Better Auth returns { redirect: true, uri: "..." }
			if (res.ok) {
				const data = (await res.json()) as { uri?: string; url?: string; redirect?: boolean }
				const redirectUrl = data.uri ?? data.url
				if (redirectUrl) {
					window.location.href = redirectUrl
					return
				}
			}

			setError('Something went wrong. Please try again.')
		} catch {
			setError('Something went wrong. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	if (loading) {
		return (
			<div className="flex justify-center py-10">
				<LoadingIndicator size="xl" type="line-simple" />
			</div>
		)
	}

	if (error && !consentInfo) {
		return (
			<div className="bg-error-secondary rounded-lg px-4 py-3">
				<p className="text-error-primary text-sm">{error}</p>
			</div>
		)
	}

	const clientName = consentInfo?.client?.name || 'An application'
	const scopes = consentInfo?.scopes || []

	// Extract redirect URI domain from query params
	const redirectUri = searchParams.get('redirect_uri')
	let redirectDomain: string | null = null
	if (redirectUri) {
		try {
			redirectDomain = new URL(redirectUri).hostname
		} catch {
			// Ignore invalid URIs
		}
	}

	return (
		<div className="z-10 flex flex-col gap-6">
			<p className="text-secondary text-md">
				<strong className="text-primary font-medium">{clientName}</strong> is
				requesting access to your Iconited account.
			</p>

			{redirectDomain && (
				<p className="text-tertiary text-sm">
					Will redirect to: <code className="text-secondary">{redirectDomain}</code>
				</p>
			)}

			{scopes.length > 0 && (
				<div className="flex flex-col gap-2">
					<p className="text-tertiary text-sm font-medium">
						This will allow the application to:
					</p>
					<ul className="flex flex-col gap-1">
						{scopes.map((scope) => (
							<li
								key={scope}
								className="text-secondary text-sm flex items-center gap-2"
							>
								<span className="text-tertiary">&#8226;</span>
								{scopeDescription(scope)}
							</li>
						))}
					</ul>
				</div>
			)}

			{error && (
				<div className="bg-error-secondary rounded-lg px-4 py-3">
					<p className="text-error-primary text-sm">{error}</p>
				</div>
			)}

			<div className="flex flex-col gap-3">
				<Button
					type="button"
					size="lg"
					isLoading={submitting}
					disabled={submitting}
					onClick={() => void handleConsent(true)}
				>
					Allow access
				</Button>
				<Button
					type="button"
					size="lg"
					color="secondary"
					disabled={submitting}
					onClick={() => void handleConsent(false)}
				>
					Deny
				</Button>
			</div>
		</div>
	)
}

function scopeDescription(scope: string): string {
	switch (scope) {
		case 'openid':
			return 'Verify your identity'
		case 'offline_access':
			return 'Stay connected (refresh tokens)'
		default:
			return scope
	}
}

export default function ConsentPage() {
	return (
		<section className="bg-primary relative min-h-screen overflow-hidden px-4 py-12 md:px-8 md:pt-24">
			<div className="relative z-10 mx-auto flex w-full flex-col gap-8 sm:max-w-90">
				<div className="flex flex-col items-center gap-6 text-center">
					<div className="z-10 flex flex-col gap-2 md:gap-3">
						<h1 className="text-display-xs text-primary md:text-display-sm font-semibold">
							Authorize Access
						</h1>
						<p className="text-md text-tertiary self-stretch p-0">
							Review the permissions being requested.
						</p>
					</div>
				</div>

				<ClientOnly
					fallback={
						<div className="flex justify-center py-10">
							<LoadingIndicator size="xl" type="line-simple" />
						</div>
					}
				>
					{() => <ConsentForm />}
				</ClientOnly>
			</div>
		</section>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Authorize - Iconited' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
